class VideoCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.video-carousel__track');
    this.items = Array.from(this.querySelectorAll('.video-carousel__item'));
    this.dots = Array.from(this.querySelectorAll('.video-carousel__dot'));
    this.current = -1;
    this.fallbackTimer = null;
    this.handleEnded = this.next.bind(this);

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.showItem(index));
    });

    this.isDragging = false;
    this.dragLocked = null;
    this.startX = 0;
    this.startY = 0;
    this.startTime = 0;
    this.baseOffset = 0;

    this.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
    this.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    this.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
    this.addEventListener('touchcancel', this.handleTouchEnd.bind(this), { passive: true });

    window.addEventListener('resize', () => {
      if (!this.isDragging && this.current >= 0) {
        this.track.style.transform = `translateX(${-this.current * this.trackWidth}px)`;
      }
    });
  }

  connectedCallback() {
    if (!this.items.length) return;

    // Shopify's video_tag puts autoplay on every slide. Stop all but slide 0
    // from trying to play immediately — avoids 6 simultaneous download races.
    this.items.forEach((item, i) => {
      const video = item.querySelector('video');
      if (!video) return;
      if (i > 0) {
        video.removeAttribute('autoplay');
        video.pause();
        if (i > 1) video.preload = 'metadata';
      }
    });

    // Eagerly buffer the first two slides so a swipe to slide 1 is instant.
    this._preloadItem(0);
    this._preloadItem(1);
    this.showItem(0);
  }

  get trackWidth() {
    return this.getBoundingClientRect().width;
  }

  _videoOf(index) {
    const item = this.items[index];
    return item ? item.querySelector('video') : null;
  }

  // Upgrade a video to preload="auto" and kick off buffering.
  // FIX: was `readyState === 0` which skipped slides set to preload="metadata"
  // (those sit at readyState 1 = HAVE_METADATA, so .load() was never called).
  // Changed to readyState <= 1 so both HAVE_NOTHING and HAVE_METADATA states
  // trigger a fresh load, which is required for buffering to actually start.
  _preloadItem(index) {
    if (index < 0 || index >= this.items.length) return;
    const video = this._videoOf(index);
    if (!video || video.preload === 'auto') return;
    video.preload = 'auto';
    if (video.readyState <= 1) video.load();
  }

  // Remove any pending canplay listener from a video to prevent stacking.
  // FIX: rapid swipes were adding multiple canplay listeners to the same video
  // element; this ensures at most one is ever pending.
  _clearCanplay(video) {
    if (video && video._pendingCanplay) {
      video.removeEventListener('canplay', video._pendingCanplay);
      video._pendingCanplay = null;
    }
  }

  // Play with a muted-retry fallback for browsers that block autoplay.
  _playVideo(video) {
    video.muted = true;
    const p = video.play();
    if (p !== undefined) {
      p.catch(() => {
        video.muted = true;
        video.play().catch(() => {});
      });
    }
  }

  handleTouchStart(event) {
    if (this.items.length < 2) return;

    const touch = event.touches[0];
    this.isDragging = true;
    this.dragLocked = null;
    this.startX = touch.clientX;
    this.startY = touch.clientY;
    this.startTime = Date.now();
    this.baseOffset = -this.current * this.trackWidth;
    this.track.classList.add('is-dragging');

    // Speculatively buffer both neighbours the instant a swipe begins —
    // the 350ms animation gives the buffer a head start.
    this._preloadItem(this.current - 1);
    this._preloadItem(this.current + 1);
  }

  handleTouchMove(event) {
    if (!this.isDragging) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - this.startX;
    const deltaY = touch.clientY - this.startY;

    if (this.dragLocked === null) {
      if (Math.abs(deltaX) < 5 && Math.abs(deltaY) < 5) return;
      this.dragLocked = Math.abs(deltaX) > Math.abs(deltaY);
      if (!this.dragLocked) {
        this.isDragging = false;
        this.track.classList.remove('is-dragging');
        return;
      }
    }

    if (!this.dragLocked) return;

    event.preventDefault();

    const atFirst = this.current === 0;
    const atLast = this.current === this.items.length - 1;
    let offset = deltaX;

    if ((atFirst && deltaX > 0) || (atLast && deltaX < 0)) {
      offset = deltaX / 3;
    }

    this.track.style.transform = `translateX(${this.baseOffset + offset}px)`;
  }

  handleTouchEnd(event) {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.track.classList.remove('is-dragging');

    const wasLocked = this.dragLocked;
    this.dragLocked = null;

    if (!wasLocked) return;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.startX;
    const elapsed = Date.now() - this.startTime;
    const velocity = elapsed > 0 ? Math.abs(deltaX) / elapsed : 0;

    const atFirst = this.current === 0;
    const atLast = this.current === this.items.length - 1;
    let targetIndex = this.current;

    if (Math.abs(deltaX) > 80 || velocity > 0.3) {
      if (deltaX < 0 && !atLast) {
        targetIndex = this.current + 1;
      } else if (deltaX > 0 && !atFirst) {
        targetIndex = this.current - 1;
      }
    }

    this.showItem(targetIndex);
  }

  showItem(index) {
    if (!this.items.length) return;

    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }

    const normalizedIndex = ((index % this.items.length) + this.items.length) % this.items.length;

    // Tear down the outgoing slide.
    const previous = this.items[this.current];
    if (previous) {
      previous.classList.remove('is-active');
      const prevVideo = previous.querySelector('video');
      const prevIframe = previous.querySelector('iframe');
      if (prevVideo) {
        // Clear any pending canplay listener before the slide goes offscreen.
        this._clearCanplay(prevVideo);
        prevVideo.pause();
        prevVideo.removeEventListener('ended', this.handleEnded);
        // FIX: defer the seek-to-zero until after the CSS slide-out animation
        // (350ms). Previously this ran immediately, causing the browser to seek
        // to an unbuffered frame *while the slide was still visible*, producing
        // a black flash on the outgoing slide during the swipe animation.
        const videoToReset = prevVideo;
        setTimeout(() => { videoToReset.currentTime = 0; }, 400);
      }
      if (prevIframe) prevIframe.src = '';
    }

    this.current = normalizedIndex;
    const active = this.items[this.current];
    active.classList.add('is-active');

    this.track.style.transform = `translateX(${-this.current * this.trackWidth}px)`;

    // Proactively buffer the slide after this one while the current plays.
    this._preloadItem(this.current + 1);

    const video = active.querySelector('video');
    const iframe = active.querySelector('iframe');

    if (video) {
      // Remove any canplay listener left over from a previous visit to this slide.
      this._clearCanplay(video);

      video.muted = true;
      video.loop = false;
      video.addEventListener('ended', this.handleEnded, { once: true });

      if (video.readyState >= 3) {
        // HAVE_FUTURE_DATA or HAVE_ENOUGH_DATA — play immediately, no poster flash.
        this._playVideo(video);
      } else {
        // Not buffered yet. Keep the poster visible (browser shows poster while
        // video hasn't played) and wait for canplay before starting.
        this._preloadItem(this.current);

        // Store handler on the element so _clearCanplay can remove it on rapid swipes.
        const handler = () => {
          video._pendingCanplay = null;
          if (this.items[this.current] === active) {
            this._playVideo(video);
          }
        };
        video._pendingCanplay = handler;
        video.addEventListener('canplay', handler, { once: true });
      }
    } else if (iframe) {
      const baseSrc = iframe.dataset.src;
      const separator = baseSrc.includes('?') ? '&' : '?';
      iframe.src = `${baseSrc}${separator}autoplay=1&mute=1`;
      this.fallbackTimer = setTimeout(this.handleEnded, 15000);
    }

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === this.current;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  next() {
    if (this.items.length < 2) return;
    this.showItem(this.current + 1);
  }

  previous() {
    if (this.items.length < 2) return;
    this.showItem(this.current - 1);
  }
}

customElements.define('video-carousel', VideoCarousel);
